import React, { useEffect } from 'react'
import { connect } from 'react-redux'

import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'

import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import MarkdownRender from 'components/Luxkit/MarkdownRender'

import config from 'constants/config'
import TextButton from 'components/Luxkit/Button/TextButton'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import {
  fetchMagazineArticles,
} from 'actions/ContentActions'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'

import { shouldShowMagazineArticle } from 'selectors/contentSelectors'
import { useAppDispatch } from 'hooks/reduxHooks'

const ArticleRow = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: ${rem(16)};
  height: 100%;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    justify-content: space-between;
    width: auto;
  }
`

const ArticleWrapper = styled.div`
  flex: 1 1 0%;
  display: flex;
  margin: 0;
  padding: 0;

  & + & {
    margin-top: ${rem(20)};
  }

  ${mediaQueryUp.tablet} {
    & + & {
      margin-top: 0;
      margin-left: ${rem(20)};
    }
  }
`
const ArticleImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: auto;

  ${mediaQueryUp.tablet} {
    height: ${rem(220)};
    object-fit: cover;
  }
`

const ArticleTitle = styled(Heading)`
  margin-top: ${rem(5)};
`

interface Props {
  content?: any;
  viewMoreUrl: string;
  axis?: 'vertical' | 'inline';
  title: string;
  isShow?: boolean;
  id?: string;
  tags: Array<string>;
  numberOfPosts?: number;
}

function MagazineArticles(props: Props) {
  const { tags, numberOfPosts, content, viewMoreUrl, axis, title, isShow, id } = props
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchMagazineArticles(tags, numberOfPosts))
  }, [dispatch, numberOfPosts, tags])

  if (!isShow) {
    return null
  }

  return (
    <Pane axis={axis} id={id}>
      <PaneBody size="xl-y">
        <LayoutContainer>
          <Heading
            variant="heading3"
            align="center"
            as="h2"
          >
            {title}
          </Heading>
          <ArticleRow>
            {content.posts.map(post =>
              <ArticleWrapper key={post.id}>
                <ClickableLink href={post.link} target="_blank" rel="noopener noreferrer">
                  <ArticleImage src={post.imageSrc} alt={post.imageAlt} />
                  <ArticleTitle variant="heading5" align="start">
                    <MarkdownRender content={post.title} />
                  </ArticleTitle>
                </ClickableLink>
              </ArticleWrapper>,
            )}
          </ArticleRow>
          <BodyText variant="large" align="center">
            <TextButton
              kind="primary"
              href={viewMoreUrl || config.MAGAZINE_PORTAL}
              target="_blank"
              rel="noopener noreferrer"
            >
              See more articles
            </TextButton>
          </BodyText>
        </LayoutContainer>
      </PaneBody>
    </Pane>
  )
}

const mapStateToProps = (state: App.State) => ({
  content: state.content.magazineArticles,
  isShow: shouldShowMagazineArticle(state),
})

export default connect(mapStateToProps)(MagazineArticles)
